import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  input,
  Type,
  ViewContainerRef,
  viewChild,
} from '@angular/core';
import {
  CUSTOM_COMPONENT_REGISTRY,
  provideCustomComponentRegistry,
  resolveComponentInputs,
} from '../../../custom-component-registry';
import { CustomComponentBlock } from '../../../model';

@Component({
  selector: 'dojo-custom-component-block-renderer',
  templateUrl: './custom-component-block-renderer.component.html',
  styleUrl: './custom-component-block-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCustomComponentRegistry()],
  host: {
    draggable: 'false',
  },
})
export class CustomComponentBlockRendererComponent {
  constructor() {
    effect(() => {
      const host = this.componentHost();
      const entry = this.registryEntry();
      const resolvedInputs = this.resolvedInputs();
      this.renderComponent(host ?? null, entry?.component ?? null, resolvedInputs);
    });
  }

  private readonly registry = inject(CUSTOM_COMPONENT_REGISTRY);
  private componentRef: ComponentRef<unknown> | null = null;

  private readonly componentHost = viewChild<unknown, ViewContainerRef>('componentHost', {
    read: ViewContainerRef,
  });

  readonly block = input.required<CustomComponentBlock>();

  readonly registryEntry = computed(() => {
    const id = this.block().component_id;
    return id ? this.registry.get(id) ?? null : null;
  });

  readonly resolvedInputs = computed(() => {
    const entry = this.registryEntry();
    const block = this.block();
    if (!entry) {
      return block.inputs;
    }
    return resolveComponentInputs(entry, block.preset, block.inputs);
  });

  readonly containerStyle = computed(() => {
    const block = this.block();
    const style: Record<string, string> = {};

    if (block.width !== null && block.width !== undefined) {
      style['width'] = `${block.width}px`;
    }
    if (block.height !== null && block.height !== undefined) {
      style['height'] = `${block.height}px`;
      style['overflow'] = 'hidden';
      style['display'] = 'flex';
      style['flex-direction'] = 'column';
    }
    if (block.padding !== null && block.padding !== undefined) {
      style['padding'] = `${block.padding}px`;
    }
    return style;
  });

  readonly wrapperStyle = computed(() => {
    const block = this.block();
    if (block.width === null || block.width === undefined) {
      return {};
    }
    switch (block.align) {
      case 'center':
        return { 'justify-content': 'center' };
      case 'right':
        return { 'justify-content': 'flex-end' };
      default:
        return { 'justify-content': 'flex-start' };
    }
  });

  private renderComponent(
    host: ViewContainerRef | null,
    componentClass: Type<unknown> | null,
    inputs: Record<string, unknown>,
  ): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }

    if (!host || !componentClass) {
      return;
    }

    host.clear();
    this.componentRef = host.createComponent(componentClass);

    const ref = this.componentRef;
    if (inputs && ref) {
      Object.entries(inputs).forEach(([key, value]) => {
        ref.setInput(key, value);
      });
    }
  }
}
